import { makeOrientationAck } from './orientation-ack'
import type { Tutorial } from '@/shared/components/tutorial/types'
import { platformWelcomeStep } from './platform-welcome-step'

/**
 * Context the manager tutorial reads. The picker passes raw counts /
 * orientation acks here so the tutorial primitive stays free of
 * cross-feature data fetching (boundaries lint).
 */
export interface ManagerSetupCtx {
  /** True once the manager has opened Reservas (its menu item or the tour's
   *  deep-link CTA) — see `isManagerReservasSeen`. Was a club-wide proxy
   *  (`bookingsTodayCount > 0`), which pre-ticked the step at any club with
   *  bookings today; now a per-user orientation ack like the others. */
  reservasSeen: boolean
  /** True once the manager has opened the Registro de actividad from this
   *  tour — see `isManagerActividadSeen`. localStorage-backed (managers
   *  can't write the club doc — see the rationale block below). */
  actividadSeen: boolean
  /** True once the manager has opened the Horario tab from this tour —
   *  see `isManagerHorarioSeen`. */
  horarioSeen: boolean
  /** True once running installed (standalone) OR the operator opened the
   * install instructions once — drives the shared platform-welcome step. */
  platformWelcomeDone: boolean
}

// Keep each manager's orientation separate on shared devices and across clubs.
const reservasAck = makeOrientationAck('manager.reservas')
const actividadAck = makeOrientationAck('manager.actividad')
const horarioAck = makeOrientationAck('manager.horario')
export const isManagerReservasSeen = reservasAck.is
export const markManagerReservasSeen = reservasAck.mark
export const isManagerActividadSeen = actividadAck.is
export const markManagerActividadSeen = actividadAck.mark
export const isManagerHorarioSeen = horarioAck.is
export const markManagerHorarioSeen = horarioAck.mark

/**
 * Manager-tier first-time tour. Mapped to the manager's audit + control
 * mindset (per `screen-role-matrix.md`): Reservas (his daily surface) →
 * Actividad (the audit trail — "who cancelled the 18:00" — his
 * identity-defining screen) → Horario (he owns the weekly schedule).
 * Install lands LAST: it's housekeeping, not orientation. The prior
 * "Invita a tu equipo" step was dropped — it overstated the manager's
 * authority (he can invite but not assign roles) and was a low-frequency
 * task; the activity log is the higher-value daily screen (Marce
 * 2026-06-13).
 */
export const MANAGER_SETUP_TUTORIAL: Tutorial<ManagerSetupCtx> = {
  id: 'manager-setup',
  title: 'Tu base como gestor',
  intro:
    'Reservas, actividad y horario — el control de la semana en tus manos.',
  steps: [
    {
      id: 'reservas',
      label: 'Reservas',
      hint: 'Consulta y filtra por fecha, estado o pista. Las acciones disponibles dependen de tus permisos.',
      isComplete: ({ reservasSeen }) => reservasSeen,
      deepLink: { label: 'Ir a Reservas', to: '/bookings' },
      onActivate: markManagerReservasSeen,
    },
    {
      id: 'actividad',
      label: 'Registro de actividad',
      hint: 'Quién hizo qué y cuándo. Filtra por persona, tipo y fecha para resolver dudas al instante.',
      isComplete: ({ actividadSeen }) => actividadSeen,
      deepLink: { label: 'Ir a Actividad', to: '/activity' },
      onActivate: markManagerActividadSeen,
    },
    {
      id: 'horario',
      label: 'Horario del club',
      hint: 'Confirma las horas de apertura. Fuera de horario pide confirmación.',
      isComplete: ({ horarioSeen }) => horarioSeen,
      deepLink: { label: 'Ir a Horario', to: '/settings?tab=hours' },
      onActivate: markManagerHorarioSeen,
    },
    platformWelcomeStep<ManagerSetupCtx>(),
  ],
}
